import { useCallback, useEffect, useRef } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bookmark, Heart } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { FavoritesView } from "@/components/account/favorites-view";
import { SearchesView } from "@/components/account/searches-view";
import { PageHeader } from "@/components/layout/page-header";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { invalidateFavoriteProviders } from "@/lib/hooks/use-favorite-providers";
import { showToast } from "@/lib/ui/toast";

type Tab = "favorites" | "searches";
const VALID_TABS: readonly Tab[] = ["favorites", "searches"] as const;

interface CollectionsSearch {
  tab: Tab;
  /** Provider to add to favorites, then dropped from the URL. Set by the www
   *  heart for a signed-out visitor: they sign in here, and the save happens.
   *  Not `add` — the `_authenticated` layout owns that one (add-entity modals). */
  favorite?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/_authenticated/account/user/collections")({
  validateSearch: (search: Record<string, unknown>): CollectionsSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    const favorite =
      typeof search.favorite === "string" && UUID_RE.test(search.favorite)
        ? search.favorite
        : undefined;
    return {
      tab: VALID_TABS.includes(raw as Tab) ? (raw as Tab) : "favorites",
      ...(favorite ? { favorite } : {}),
    };
  },
  component: CollectionsPage,
});

/**
 * Collections — 2-tab hub (Favorites + Saved searches).
 *
 * R9 audit fix: Day 7 stub had a Favorites tab that just linked out to
 * /favorites + a V1.1-placeholder Searches tab. Restored to legacy 1:1
 * with both views embedded inline (legacy CollectionsTabs pattern).
 */
function CollectionsPage() {
  const { t } = useLingui();
  const { tab: activeTab, favorite: addFavoriteId } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  // One save per provider id, also under StrictMode's double effect.
  const addedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!addFavoriteId || addedRef.current === addFavoriteId) return;
    addedRef.current = addFavoriteId;
    apiFetch
      .post("/api/account/favorites", { providerId: addFavoriteId, favorite: true })
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ["api", "/api/account/favorites"] });
        invalidateFavoriteProviders();
        showToast(t`Added to your favorite doctors.`);
      })
      .catch(() => showToast(t`Could not add this doctor to your favorites.`, "error"))
      .finally(() => {
        void navigate({
          to: "/account/user/collections",
          search: { tab: "favorites" },
          replace: true,
        });
      });
  }, [addFavoriteId, navigate, queryClient, t]);

  const setTab = useCallback(
    (next: Tab) => navigate({ to: "/account/user/collections", search: { tab: next } }),
    [navigate],
  );

  const tabs: TabItem<Tab>[] = [
    { id: "favorites", label: t`Favorite doctors`, icon: <Heart className="size-4" /> },
    { id: "searches", label: t`Saved searches`, icon: <Bookmark className="size-4" /> },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        title={<Trans>Saved collections</Trans>}
        description={<Trans>Your favorite doctors and saved searches in one place</Trans>}
      />

      {/* Canonical tabs spacing: TabBar mt-6, content mt-4. */}
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={VALID_TABS}
        onTabChange={setTab}
        className="mt-4"
        renderPanel={(panelKey) => (
          <div className="space-y-6">
            {panelKey === "favorites" ? (
              <FavoritesView showHeader={false} />
            ) : (
              <SearchesView showHeader={false} />
            )}
            <AccountSecurityBanner />
          </div>
        )}
      />
    </div>
  );
}
